<template>
  <div class="board" @mouseenter="hover = true" @mouseleave="hover = false">
    <div :style="gridStyle" class="board-background">
      <drop
        v-for="cell in totalCells"
        :key="cell"
        :style="cellStyle"
        class="board-background__cell"
        @dragover="(data) => handleDragover(cell - 1, data)"
        @drop="(data) => handleDrop(cell - 1, data)"
      />
    </div>
    <div
      v-for="(cell, idx) in preparedCells"
      :key="idx"
      :style="cell.style"
      class="board-active__cell"
    />
    <Piece
      v-if="showPhantom"
      :index="0"
      :piece="{
        ref: phantom,
        x: phantomX,
        y: phantomY,
      }"
      :style="phantomStyle"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import * as boardConstants from "@/helpers/board-constants.js";
import { PIECES } from "@/helpers/pieces.js";
import { cellToPos, to2d } from "@/helpers/utils.js";

import Piece from "./Piece.vue";

export default {
  name: "Board",
  components: { Piece },
  data () {
    return {
      hover: false,
      phantomX: -1,
      phantomY: -1,
    };
  },
  computed: {
    ...mapState({
      board: (state) => state.room.room.board,
      phantom: (state) => state.room.phantom
    }),
    ...mapGetters("room", ["placementIsValid"]),
    totalCells () {
      return boardConstants.GRID_SIZE * boardConstants.GRID_SIZE;
    },
    preparedCells () {
      let style, p;
      return this.board
        .map((ref, idx) => {
          if (ref === null) return null;
          p = PIECES[ref];
          const { x, y } = to2d(idx);
          style = {
            left: `${x * (boardConstants.CELL_SIZE + boardConstants.CELL_MARGIN)}px`,
            top: `${y * (boardConstants.CELL_SIZE + boardConstants.CELL_MARGIN)}px`,
            backgroundColor: p.color,
            width: `${boardConstants.CELL_SIZE}px`,
            height: `${boardConstants.CELL_SIZE}px`,
          };
          return { style };
        })
        .filter((c) => c !== null);
    },
    showPhantom () {
      return this.phantom && this.phantomX >= 0 && this.phantomY >= 0;
    },
    gridStyle () {
      return {
        'grid-template-columns': `repeat(${boardConstants.GRID_SIZE}, ${boardConstants.CELL_SIZE}px)`,
      };
    },
    cellStyle () {
      return {
        width: `${boardConstants.CELL_SIZE}px`,
        height: `${boardConstants.CELL_SIZE}px`,
      };
    },
    phantomStyle () {
      return {
        position: "absolute",
        opacity: 0.35,
        brightness: "120%",
        left: `${(this.phantomX * (boardConstants.CELL_SIZE + boardConstants.CELL_MARGIN))}px`,
        top: `${(this.phantomY * (boardConstants.CELL_SIZE + boardConstants.CELL_MARGIN))}px`,
      };
    },
  },
  methods: {
    handleDragover(p, data) {
      const res = cellToPos(p, data);
      if (!res) return;
      this.phantomX = res.x;
      this.phantomY = res.y;
    },
    handleDrop (p, data) {
      const res = cellToPos(p, data);
      if (!res) return;
      const x = res.x;
      const y = res.y;
      const cells = res.cells;
      if (this.placementIsValid({ x, y, cells })) {
        this.$store.dispatch("room/placePiece", { ref: data.ref, index: data.index, x, y, cells });
      }
      this.phantomX = -1;
      this.phantomY = -1;
    },
  },
}
</script>

<style scoped>
.board {
  position: relative;
  margin-top: 48px;
}

.board-background {
  display: grid;
  grid-gap: 2px;
}

.board-active__cell {
  position: absolute;
  z-index: 2;
}
</style>
